import { useId } from "react";
import type { ReactNode } from "react";
import { motion } from "motion/react";
import type { SlideProps } from "@/engine/types";
import { GlassCard, GradientText, RevealItem, RevealList, SlideShell, useAccent } from "@/ui";

/** Timing shared between the row reveals and the self-ticking checkboxes. */
const START = 0.2;
const STAGGER = 0.35;
const TICK_LAG = 0.4;

const ITEMS: { key: string; text: ReactNode }[] = [
  {
    key: "gsc",
    text: (
      <>
        Pick <GradientText className="font-semibold">one property</GradientText> in GSC — read{" "}
        <span className="font-semibold text-white">Performance</span> and{" "}
        <span className="font-semibold text-white">Page indexing</span> once a week for a month.
      </>
    ),
  },
  {
    key: "checklist",
    text: (
      <>
        Ship one <span className="font-semibold text-white">on-page checklist pass</span> on your{" "}
        <GradientText className="font-semibold">highest-traffic template</GradientText>.
      </>
    ),
  },
  {
    key: "decks",
    text: (
      <>
        Read the <span className="font-semibold text-white">intermediate decks</span> when
        you&rsquo;re ready to go deeper on{" "}
        <GradientText className="font-semibold">E-E-A-T, schema, and CWV</GradientText>.
      </>
    ),
  },
  {
    key: "log",
    text: (
      <>
        Document <GradientText className="font-semibold">wins and failures</GradientText> in a{" "}
        <span className="font-semibold text-white">short log</span> so the next session has
        concrete examples.
      </>
    ),
  },
];

/** Square checkbox that ticks itself in sync with its row's entrance. */
function AnimatedCheck({ active, delay }: { active: boolean; delay: number }) {
  const gradId = useId();
  const [from, to] = useAccent();
  return (
    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/15 bg-white/5">
      <motion.div
        className="absolute inset-0"
        style={{ backgroundImage: `linear-gradient(135deg, ${from}2e, ${to}2e)` }}
        initial={{ opacity: 0 }}
        animate={active ? { opacity: 1 } : { opacity: 0 }}
        transition={active ? { delay, duration: 0.35 } : { duration: 0.2 }}
      />
      <svg viewBox="0 0 24 24" className="relative h-8 w-8" fill="none" aria-hidden>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
            <stop stopColor={from} />
            <stop offset="1" stopColor={to} />
          </linearGradient>
        </defs>
        <motion.path
          d="M4.5 12.5l5 5L19.5 7"
          stroke={`url(#${gradId})`}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={active ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
          transition={active ? { delay, duration: 0.5, ease: "easeOut" } : { duration: 0.2 }}
        />
      </svg>
    </div>
  );
}

export function NextStepsSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={7} title="Next steps after this block">
      <RevealList
        active={active}
        stagger={STAGGER}
        delay={START}
        className="mx-auto flex w-full max-w-5xl flex-col gap-6"
      >
        {ITEMS.map((item, i) => (
          <RevealItem key={item.key}>
            <GlassCard className="flex items-center gap-7 px-8 py-6 transition-colors duration-300 hover:bg-white/10">
              <AnimatedCheck active={active} delay={START + i * STAGGER + TICK_LAG} />
              <p className="text-2xl leading-relaxed text-white/90">{item.text}</p>
            </GlassCard>
          </RevealItem>
        ))}
      </RevealList>
    </SlideShell>
  );
}
